/**
 * Typo-tolerant phrase matching for the assistant's **read** path.
 *
 * Deliberately loose, and deliberately not exported to the write path. A fuzzy
 * match that picks the wrong question costs a wrong sentence; a fuzzy match
 * that picks the wrong field would cost a wrong live listing. Instruction
 * parsing stays exact for that reason.
 *
 * Matching is per token rather than over the raw string, so a typo cannot slide
 * a phrase across a word boundary, and the phrase's words must still appear in
 * order and adjacent.
 */

/** Splits on anything that is not a letter or digit, dropping empties. */
export const tokenize = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * Damerau–Levenshtein distance (optimal string alignment), abandoned as soon as
 * it provably exceeds `max`.
 *
 * Transposition counts as one edit rather than two, because swapping adjacent
 * letters is the most common typing mistake — plain Levenshtein would put
 * "mnay" two edits from "many" and need a slacker bound to catch it, which
 * would in turn admit genuinely different words.
 *
 * The bound matters because this runs over every synonym of every registered
 * question on each message; without it a long message would do far more work
 * than the answer is worth.
 */
export const boundedDistance = (a: string, b: string, max: number): number => {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let twoBack: number[] = [];
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);

  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    let rowBest = i;

    for (let j = 1; j <= b.length; j += 1) {
      const substitution = previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      let best = Math.min(current[j - 1] + 1, previous[j] + 1, substitution);

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, twoBack[j - 2] + 1);
      }

      current.push(best);
      rowBest = Math.min(rowBest, best);
    }

    // Every later row is >= the best of this one, so the bound can be settled here.
    if (rowBest > max) return max + 1;
    twoBack = previous;
    previous = current;
  }

  return previous[b.length];
};

/**
 * How far apart two words may be and still count as the same word.
 *
 * Short words get no slack at all: at two characters "on" and "of" are one edit
 * apart but mean opposite things, and "do" / "go" would collapse together.
 */
const allowedDistance = (length: number): number => {
  if (length < 3) return 0;
  if (length < 7) return 1;
  return 2;
};

/** True when two words match exactly or differ by a tolerable typo. */
export const tokensSimilar = (a: string, b: string): boolean => {
  if (a === b) return true;
  const max = allowedDistance(Math.max(a.length, b.length));
  return max > 0 && boundedDistance(a, b, max) <= max;
};

/**
 * True when `phrase`'s words appear in `tokens` consecutively and in order,
 * each allowing a typo.
 */
export const containsFuzzyPhrase = (tokens: string[], phrase: string[]): boolean => {
  if (phrase.length === 0 || phrase.length > tokens.length) return false;

  for (let start = 0; start <= tokens.length - phrase.length; start += 1) {
    let matched = true;

    for (let offset = 0; offset < phrase.length; offset += 1) {
      if (!tokensSimilar(tokens[start + offset], phrase[offset])) {
        matched = false;
        break;
      }
    }

    if (matched) return true;
  }

  return false;
};
